/**
 * Small module for all Twilio-related API & edge function calls, for onboarding.
 */
import { supabase } from "@/integrations/supabase/client";

/**
 * Syncs Twilio numbers using the Calie_syncNumbers edge function.
 */
export async function syncTwilioNumbersEdge(tenant_id: string, twilio_sid: string) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error("No session found");

  const res = await fetch(
    "https://ktywphxmqafinzfgzhps.functions.supabase.co/Calie_syncNumbers",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ tenant_id, twilio_sid }),
    }
  );
  const responseData = await res.json();
  if (!res.ok || !responseData.success) {
    throw new Error(responseData.error || "Failed to sync numbers from Twilio");
  }
  return true;
}

/**
 * Validates Twilio credentials via edge function.
 */
export async function validateTwilioCredentialsEdge(sid: string, authToken: string): Promise<boolean> {
  const { data } = await supabase.auth.getSession();
  const currentSession = data.session;

  if (!currentSession) {
    throw new Error("Session expired. Please refresh and try again.");
  }

  const res = await fetch(
    `https://ktywphxmqafinzfgzhps.functions.supabase.co/Calie_twilioValidate`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${currentSession.access_token}`,
      },
      body: JSON.stringify({ accountSid: sid, authToken }),
    }
  );
  const responseData = await res.json();

  if (res.status === 401) {
    throw new Error("Session expired. Please refresh and try again.");
  }
  if (res.status === 400) {
    throw new Error(responseData.error || "Invalid format for Twilio credentials.");
  }
  if (res.ok && !responseData.success) {
    throw new Error(responseData.error || "Invalid Twilio credentials. Please check and try again.");
  }
  if (!res.ok) {
    throw new Error(responseData.error || "Error validating credentials. Please try again.");
  }

  return responseData.success === true;
}

/**
 * Purchases a Twilio number via the Calie_buyNumber edge function.
 * Returns the purchased E.164 number on success.
 */
export async function buyTwilioNumberEdge(areaCode: string, country: string = 'US'): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error("No session found");

  const res = await fetch(
    "https://ktywphxmqafinzfgzhps.functions.supabase.co/Calie_buyNumber",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ areaCode, country }),
    }
  );

  const responseData = await res.json();
  if (!res.ok || !responseData.success) {
    throw new Error(responseData.error || "Failed to purchase a number");
  }
  return responseData.e164 as string;
}

/**
 * Configure voice webhooks for existing tenant numbers
 */
export async function configureVoiceWebhooksEdge(): Promise<boolean> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error("No session found");

  const res = await fetch(
    "https://ktywphxmqafinzfgzhps.functions.supabase.co/Calie_configureVoiceWebhook",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({}),
    }
  );

  const responseData = await res.json();
  if (!res.ok || !responseData.success) {
    throw new Error(responseData.error || "Failed to configure webhooks");
  }
  
  return true;
}

/**
 * Purchases a number and assigns it to an Ad via edge function.
 */
export async function buyNumberForAdEdge(adId: string, params: { country?: string; areaCode?: string; search?: string }) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error("No session found");

  const res = await fetch(
    "https://ktywphxmqafinzfgzhps.functions.supabase.co/Calie_buyNumberForAd",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ adId, ...params }),
    }
  );
  const responseData = await res.json();
  if (!res.ok || !responseData.success) {
    throw new Error(responseData.error || "Failed to buy number for ad");
  }
  return responseData; // { success: true, e164, number_id }
}

/**
 * Manually trigger transcription for a call
 */
export async function transcribeCallEdge(callId: string, recordingUrl: string) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error("No session found");

  const res = await fetch(
    "https://ktywphxmqafinzfgzhps.functions.supabase.co/Calie_transcribe",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ callId, recordingUrl }),
    }
  );
  const responseData = await res.json();
  if (!res.ok || !responseData.success) {
    throw new Error(responseData.error || "Failed to transcribe call");
  }
  return responseData;
}

/**
 * Manually trigger AI analysis for a call
 */
export async function summarizeCallEdge(callId: string) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error("No session found");

  const res = await fetch(
    "https://ktywphxmqafinzfgzhps.functions.supabase.co/Calie_summarize",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ callId }),
    }
  );
  const responseData = await res.json();
  if (!res.ok || !responseData.success) {
    throw new Error(responseData.error || "Failed to analyze call");
  }
  return responseData;
}

/**
 * Send missed call SMS
 */
export async function sendMissedCallSMSEdge(callId: string, fromNumber: string, tenantId: string) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error("No session found");

  const res = await fetch(
    "https://ktywphxmqafinzfgzhps.functions.supabase.co/Calie_missedCallSMS",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ callId, fromNumber, tenantId }),
    }
  );
  const responseData = await res.json();
  if (!res.ok || !responseData.success) {
    throw new Error(responseData.error || "Failed to send missed call SMS");
  }
  return responseData;
}